import { useCallback, useEffect, useState } from 'react';
import { api, errorText } from './lib/api';
import { cacheGet, cacheSet } from './lib/store';
import { onSync, type SyncState } from './lib/sync';
import { classNames, go, useRoute, useServerEvents } from './lib/util';
import { Login } from './views/Login';
import { Campaigns } from './views/Campaigns';
import { Dashboard } from './views/Dashboard';
import { Records } from './views/Records';
import { EntityDetail } from './views/EntityDetail';
import { SearchView } from './views/Search';
import { Documents } from './views/Documents';
import { DocumentDetail } from './views/DocumentDetail';
import { History } from './views/History';
import { Exports } from './views/Exports';
import { CampaignSettings } from './views/CampaignSettings';
import { AppSettings } from './views/AppSettings';

export interface AiStatus {
  state: 'online' | 'offline' | 'paused' | 'model_missing' | 'disabled' | 'unknown';
  paused: boolean;
  checkedAt: string | null;
  models: { chat: boolean; embed: boolean; vision: boolean };
  error?: string;
}

type Auth = 'checking' | 'in' | 'out' | 'setup';

export function App() {
  const [auth, setAuth] = useState<Auth>('checking');
  const route = useRoute();

  const check = useCallback(async () => {
    try {
      const s = await api<{ authenticated: boolean; setupRequired: boolean }>('/api/auth/status');
      setAuth(s.authenticated ? 'in' : s.setupRequired ? 'setup' : 'out');
    } catch {
      // Offline: trust the last known state so captured notes stay reachable.
      setAuth(localStorage.getItem('trpg.signedIn') === '1' ? 'in' : 'out');
    }
  }, []);
  useEffect(() => {
    void check();
    const on = () => setAuth('out');
    window.addEventListener('trpg:auth-required', on);
    return () => window.removeEventListener('trpg:auth-required', on);
  }, [check]);
  useEffect(() => {
    if (auth === 'in') localStorage.setItem('trpg.signedIn', '1');
    if (auth === 'out') localStorage.removeItem('trpg.signedIn');
  }, [auth]);

  if (auth === 'checking') return <div className="center muted">Loading…</div>;
  if (auth !== 'in') return <Login setup={auth === 'setup'} onDone={() => setAuth('in')} />;

  const [section, cid, sub, id, extra] = route;
  let view: React.ReactNode;
  if (section === 'c' && cid) {
    if (!sub || sub === 's') view = <Dashboard campaignId={cid} sessionId={sub === 's' ? id : undefined} />;
    else if (sub === 'records' && id === 'e' && extra) view = <EntityDetail campaignId={cid} entityId={extra} />;
    else if (sub === 'records') view = <Records campaignId={cid} type={id} />;
    else if (sub === 'search') view = <SearchView campaignId={cid} />;
    else if (sub === 'documents' && id) view = <DocumentDetail campaignId={cid} documentId={id} />;
    else if (sub === 'documents') view = <Documents campaignId={cid} />;
    else if (sub === 'history') view = <History campaignId={cid} />;
    else if (sub === 'export') view = <Exports campaignId={cid} />;
    else if (sub === 'settings') view = <CampaignSettings campaignId={cid} />;
  } else if (section === 'settings') view = <AppSettings onSignedOut={() => setAuth('out')} />;
  view ??= <Campaigns />;

  return (
    <Shell campaignId={section === 'c' ? cid : null} active={section === 'c' ? sub ?? '' : section ?? ''}>
      {view}
    </Shell>
  );
}

const NAV: [string, string][] = [
  ['', 'Session'],
  ['records', 'Records'],
  ['search', 'Search & ask'],
  ['documents', 'Documents'],
  ['history', 'History'],
  ['export', 'Export'],
  ['settings', 'Campaign'],
];

function Shell({ campaignId, active, children }: { campaignId: string | null; active: string; children: React.ReactNode }) {
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [menu, setMenu] = useState(false);
  useEffect(() => {
    (async () => {
      const cached = await cacheGet<any[]>('campaigns');
      if (cached) setCampaigns(cached);
      try {
        const list = await api<any[]>('/api/campaigns');
        setCampaigns(list);
        void cacheSet('campaigns', list);
      } catch {
        /* offline: cached list */
      }
    })();
  }, [campaignId]);
  useEffect(() => setMenu(false), [active, campaignId]);
  const current = campaigns.find((c) => c.id === campaignId);
  return (
    <div className="app">
      <header className="topbar">
        <a className="brand" href="#/" aria-label="All campaigns">
          <img src="/icon.svg" alt="" width={26} height={26} />
          <span className="brand-name">{current ? current.name : 'TRPG Notes'}</span>
        </a>
        {campaignId && (
          <button className="icon-btn menu-btn" aria-expanded={menu} aria-controls="mainnav" onClick={() => setMenu(!menu)}>
            Menu
          </button>
        )}
        {campaignId && (
          <nav id="mainnav" className={classNames('mainnav', menu && 'open')} aria-label="Campaign sections">
            {NAV.map(([k, label]) => (
              <a key={k} href={`#/c/${campaignId}${k ? `/${k}` : ''}`} className={classNames(active === k || (k === '' && active === 's') ? 'active' : '')} aria-current={active === k ? 'page' : undefined}>
                {label}
              </a>
            ))}
            <a href="#/" className="nav-sep">
              All campaigns
            </a>
          </nav>
        )}
        <div className="topbar-status">
          <SyncBadge />
          <AiPill />
          <a className="icon-btn" href="#/settings" title="App settings" aria-label="App settings">
            ⚙
          </a>
        </div>
      </header>
      <main className="main">{children}</main>
    </div>
  );
}

export function useAiStatus(): [AiStatus | null, (s: AiStatus) => void, any] {
  const [ai, setAi] = useState<AiStatus | null>(null);
  const [queue, setQueue] = useState<any>(null);
  const load = useCallback(async () => {
    try {
      const s = await api<any>('/api/status');
      setAi(s.ai);
      setQueue(s.queue);
    } catch {
      setAi(null);
    }
  }, []);
  useEffect(() => {
    void load();
    const t = window.setInterval(load, 15_000);
    return () => clearInterval(t);
  }, [load]);
  useServerEvents(null, (what, d) => {
    if (what === 'ai' && d.ai) setAi(d.ai);
    if (what === 'jobs' || what === 'ai') void load();
  });
  return [ai, setAi, queue];
}

const AI_TEXT: Record<string, string> = {
  online: 'AI ready',
  offline: 'AI offline',
  paused: 'AI paused',
  model_missing: 'AI model missing',
  disabled: 'AI not configured',
  unknown: 'AI checking…',
};

function AiPill() {
  const [ai, setAi, queue] = useAiStatus();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const state = ai?.state ?? 'server';
  const waiting = queue?.waitingForAi ?? 0;
  const toggle = async () => {
    setBusy(true);
    setErr('');
    try {
      const r = await api<any>('/api/ai/pause', { body: { paused: !ai?.paused } });
      setAi(r.ai);
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="popover-wrap">
      <button className={classNames('pill', `ai-${state}`)} onClick={() => setOpen(!open)} aria-expanded={open} aria-haspopup="dialog">
        <span className="dot" aria-hidden="true" />
        {ai ? AI_TEXT[ai.state] : 'Server unreachable'}
        {waiting > 0 && <span className="count">{waiting}</span>}
      </button>
      {open && (
        <div className="popover" role="dialog" aria-label="AI status">
          <p>
            <strong>{ai ? AI_TEXT[ai.state] : 'Server unreachable'}</strong>
          </p>
          {ai?.state === 'offline' && <p className="muted">The inference PC looks asleep, off or unreachable. Notes are still saved; AI work waits in the queue.</p>}
          {ai?.state === 'paused' && <p className="muted">AI processing is paused. Notes are saved and queued; resume when you are done gaming.</p>}
          {ai?.state === 'model_missing' && <p className="muted">{ai.error}</p>}
          {!ai && <p className="muted">Captured notes are kept on this device and sync when the server is back.</p>}
          <p className="muted">{waiting} job{waiting === 1 ? '' : 's'} waiting for AI.</p>
          {ai && (
            <button className="btn" onClick={toggle} disabled={busy}>
              {ai.paused ? 'Resume AI processing' : 'Pause AI (release GPU)'}
            </button>
          )}
          {err && <p className="error">{err}</p>}
          <p>
            <a href="#/settings" onClick={() => setOpen(false)}>
              AI settings & queue
            </a>
          </p>
        </div>
      )}
    </div>
  );
}

function SyncBadge() {
  const [s, setS] = useState<SyncState | null>(null);
  useEffect(() => onSync(setS), []);
  if (!s) return null;
  const label = s.failed.length ? `${s.failed.length} not synced` : s.pending ? `${s.pending} saved locally` : s.online ? 'Synced' : 'Offline';
  return (
    <a href="#/settings" className={classNames('pill', s.failed.length ? 'sync-failed' : s.pending ? 'sync-pending' : s.online ? 'sync-ok' : 'sync-offline')} title={s.lastError ?? undefined}>
      <span className="dot" aria-hidden="true" />
      {label}
    </a>
  );
}

export { go };
